import React, { useEffect, useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../../lib/utils";
import { ChevronDown, X } from "lucide-react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import TooltipWrapper from "../../TooltipWrapper";

function UserMultiSelect({
  users = [],
  selectedIds = [],
  onChange,
  disabled = false,
  loading = false,
  error = false,
  placeholder = "Select users",
  emptyMessage = "No users found",
  errorMessage = "Failed to load users",
  warningIds = [],
  warningDetails = {},
  className,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      if (user && user.id) {
        map.set(user.id, user);
      }
    });
    return map;
  }, [users]);

  const selectedUsers = useMemo(
    () =>
      selectedIds
        .map((id) => {
          if (usersById.has(id)) {
            return usersById.get(id);
          }
          return null;
        })
        .filter(Boolean),
    [selectedIds, usersById]
  );

  const warningIdSet = useMemo(() => new Set(warningIds), [warningIds]);
  const warningDetailMap = useMemo(() => warningDetails || {}, [warningDetails]);

  const formatTeamName = (name) => {
    if (!name) return "another team";
    const trimmed = String(name).trim();
    return /team$/i.test(trimmed) ? trimmed : `${trimmed} team`;
  };

  const toggleUser = (userId) => {
    if (!onChange) return;

    const exists = selectedIds.includes(userId);
    const nextValue = exists ? selectedIds.filter((id) => id !== userId) : [...selectedIds, userId];
    onChange(nextValue);
  };

  const removeUser = (userId) => {
    if (!onChange) return;
    const nextValue = selectedIds.filter((id) => id !== userId);
    onChange(nextValue);
  };

  const handleOpenChange = (nextOpen) => {
    if (disabled) return;
    setOpen(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full min-h-[40px] items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition focus:outline-none",
            disabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:border-[#D0D5DD]",
            triggerClassName,
            className
          )}
          disabled={disabled}
        >
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {selectedUsers.length > 0 ? (
              selectedUsers.map((user) => (
                <span
                  key={user.id}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium",
                    warningIdSet.has(user.id)
                      ? "bg-warning-100"
                      : "border-border bg-gray-50 text-gray-700"
                  )}
                >
                  {warningIdSet.has(user.id) && (
                    <TooltipWrapper
                      position="right"
                      content={
                        <>
                          <p>User is a part of the {formatTeamName(warningDetailMap[user.id])}.</p>
                          <p className="mt-1">Continuing will switch the user to the new team.</p>
                        </>
                      }
                    >
                      <InfoCircledIcon width={14} height={14} className="text-warning-600 font-bold" />
                    </TooltipWrapper>
                  )}
                  <span>{user.name || user.email || user.id}</span>
                 
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeUser(user.id);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-500">
                {loading ? "Loading users..." : placeholder}
              </span>
            )}
          </div>

          <div className="ml-2 flex items-center gap-2 text-gray-500">
            {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />}
            <ChevronDown className="h-4 w-4" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <div className="p-2">
            <div className="rounded-md border border-border">
              <CommandInput placeholder="Search users" className="h-9" />
            </div>
          </div>
          <CommandList>
            <CommandEmpty className="py-6 text-center text-sm text-gray-500">
              {loading ? "Searching..." : emptyMessage}
            </CommandEmpty>
            {error ? (
              <div className="px-4 py-6 text-sm text-red-500">{errorMessage}</div>
            ) : loading ? (
              <div className="px-4 py-6 text-sm text-gray-500">Loading users...</div>
            ) : (
              <CommandGroup>
                {users.map((user) => {
                  if (!user || !user.id) return null;
                  const isSelected = selectedIds.includes(user.id);

                  return (
                    <CommandItem
                      key={user.id}
                      value={`${user.name || ""} ${user.email || ""}`.trim() || user.id}
                      onSelect={() => toggleUser(user.id)}
                      className="flex items-start gap-3 px-3 py-2"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleUser(user.id)}
                        onClick={(event) => event.stopPropagation()}
                        className="mt-1"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-950">
                          {user.name || user.email || user.id}
                          {user.note && (
                            <span className="ml-2 text-xs text-gray-500">{user.note}</span>
                          )}
                        </span>
                        {user.email && user.name && (
                          <span className="text-xs text-gray-500">{user.email}</span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default UserMultiSelect;
